import { useState, useEffect } from "react";
import Api from '../api/Api';

const WHATSAPP_NUMBER = "+244946404569";
const COLORS = {
  primary: "#FF6B00",
  primaryDark: "#E55A00",
  accent: "#FFD600",
  bg: "#F4F6FA",
  dark: "#0F0C29",
  card: "#FFFFFF",
  text: "#111827",
  muted: "#6B7280",
  border: "#E5E7EB",
  success: "#00C853",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPrice(val) {
  const n = parseFloat(val);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function calcDiscount(price, original) {
  return Math.round((1 - parseFloat(price) / parseFloat(original)) * 100);
}

function buildWhatsAppMessage(product, form) {
  const discount = calcDiscount(product.preco, product.anterior);
  const total = (parseFloat(product.preco) * form.qty).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const msg =
    `🛒 *NOVA ENCOMENDA – SATEC IMPORTS*\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n\n` +
    `📦 *Produto:* ${product.nome}\n` +
    `🏷️ *Categoria:* ${product.categoria}\n` +
    `🔢 *Quantidade:* ${form.qty}\n` +
    `💰 *Preço unitário:* Kz ${formatPrice(product.preco)}\n` +
    `🔖 *Desconto:* ${discount}% OFF\n` +
    `💵 *Total estimado:* Kz ${total}\n\n` +
    `━━━━━━━━━━━━━━━━━━━━━━\n` +
    `👤 *Dados do Cliente*\n` +
    `• Nome: ${form.name}\n` +
    `• Telefone: ${form.phone}\n` +
    `• Endereço: ${form.address}\n` +
    (form.note ? `• Nota: ${form.note}\n` : "") +
    `\n📩 Aguardo confirmação para finalizar a importação. Obrigado! 🙏`;
  return encodeURIComponent(msg);
}

function openWhatsApp(product, form) {
  const msg = buildWhatsAppMessage(product, form);
  window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${msg}`, "_blank");
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{
      background: "#fff", borderRadius: 20, overflow: "hidden",
      boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
    }}>
      <div style={{ height: 200, background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
      <div style={{ padding: 20 }}>
        {[80, 60, 100, 40].map((w, i) => (
          <div key={i} style={{ height: 14, width: `${w}%`, background: "#f0f0f0", borderRadius: 8, marginBottom: 10, animation: "shimmer 1.4s infinite" }} />
        ))}
        <div style={{ height: 44, background: "#f0f0f0", borderRadius: 12, marginTop: 16, animation: "shimmer 1.4s infinite" }} />
      </div>
    </div>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────

function ProductCard({ product, onOrder }) {
  const [hovered, setHovered] = useState(false);
  const discount = calcDiscount(product.preco, product.anterior);
  const savings = parseFloat(product.anterior) - parseFloat(product.preco);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: hovered
          ? `0 24px 64px rgba(0,0,0,0.14), 0 0 0 2px ${COLORS.primary}`
          : "0 2px 16px rgba(0,0,0,0.07)",
        transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        cursor: "default",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Image */}
      <div style={{ position: "relative", height: 200, overflow: "hidden", background: "#F8FAFC", flexShrink: 0 }}>
        <img
          src={product.img}
          alt={product.nome}
          loading="lazy"
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            transform: hovered ? "scale(1.07)" : "scale(1)",
            transition: "transform 0.5s ease",
          }}
          onError={e => { e.target.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&q=80"; }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.28) 0%, transparent 55%)" }} />

        {product.badge && (
          <div style={{
            position: "absolute", top: 12, left: 12,
            background: product.badgecolor || COLORS.primary,
            color: "#fff", fontSize: 10, fontWeight: 800,
            padding: "4px 12px", borderRadius: 20,
            fontFamily: "'Nunito', sans-serif",
            letterSpacing: 0.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}>{product.badge}</div>
        )}

        <div style={{
          position: "absolute", top: 12, right: 12,
          background: "linear-gradient(135deg, #FF6B00, #FF3D00)",
          color: "#fff", fontSize: 11, fontWeight: 900,
          padding: "4px 10px", borderRadius: 20,
          fontFamily: "'Nunito', sans-serif",
          boxShadow: "0 2px 8px rgba(255,107,0,0.4)",
        }}>-{discount}%</div>

        {product.tag && (
          <div style={{
            position: "absolute", bottom: 10, left: 12,
            background: "rgba(255,255,255,0.94)",
            color: "#374151", fontSize: 10, fontWeight: 700,
            padding: "3px 10px", borderRadius: 20,
            fontFamily: "'Nunito', sans-serif",
            backdropFilter: "blur(4px)",
          }}>{product.tag}</div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
          {product.categoria}
        </div>

        <h3 style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: 15, fontWeight: 800, color: COLORS.text,
          marginBottom: 6, lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
          flex: 1,
        }}>{product.nome}</h3>

        <p style={{
          fontSize: 12, color: COLORS.muted, lineHeight: 1.7,
          marginBottom: 14, fontFamily: "'Nunito', sans-serif",
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{product.descricao}</p>

        {/* Price */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{
              fontSize: 22, fontWeight: 900, color: COLORS.primary,
              fontFamily: "'Nunito', sans-serif",
            }}>
              Kz {formatPrice(product.preco)}
            </span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.muted, fontFamily: "'Nunito', sans-serif", marginTop: 2 }}>
            De: <span style={{ textDecoration: "line-through" }}>Kz {formatPrice(product.anterior)}</span>
            <span style={{ color: COLORS.success, fontWeight: 700, marginLeft: 6 }}>
              Poupa Kz {formatPrice(savings)}
            </span>
          </div>
        </div>

        <button
          onClick={() => onOrder(product)}
          style={{
            width: "100%",
            background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
            border: "none", color: "#fff",
            fontFamily: "'Nunito', sans-serif",
            fontSize: 14, fontWeight: 800,
            padding: "14px",
            borderRadius: 12,
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: hovered ? "0 8px 24px rgba(255,107,0,0.5)" : "0 4px 12px rgba(255,107,0,0.3)",
            letterSpacing: 0.3,
            marginTop: "auto",
          }}
        >
          🛒 Encomendar Agora
        </button>
      </div>
    </div>
  );
}

// ─── Standalone Input (must be outside OrderModal to avoid re-mount on re-render) ──

function ModalInput({ field, label, placeholder, type = "text", value, onChange, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, color: COLORS.text, display: "block", marginBottom: 6 }}>{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(field, e.target.value)}
        style={{
          width: "100%", padding: "12px 16px", borderRadius: 10,
          border: `2px solid ${error ? "#EF4444" : COLORS.border}`,
          outline: "none", fontFamily: "'Nunito', sans-serif",
          fontSize: 13, color: COLORS.text, boxSizing: "border-box",
          transition: "border-color 0.2s",
        }}
        onFocus={e => e.target.style.borderColor = COLORS.primary}
        onBlur={e => e.target.style.borderColor = error ? "#EF4444" : COLORS.border}
      />
      {error && <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4, fontFamily: "'Nunito', sans-serif", fontWeight: 700 }}>{error}</div>}
    </div>
  );
}

// ─── Order Modal ──────────────────────────────────────────────────────────────

function OrderModal({ product, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", qty: 1, note: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  if (!product) return null;
  const discount = calcDiscount(product.preco, product.anterior);
  const total = parseFloat(product.preco) * form.qty;

  const handleFieldChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Nome obrigatório";
    if (!form.phone.trim()) e.phone = "Telefone obrigatório";
    if (!form.address.trim()) e.address = "Endereço obrigatório";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    openWhatsApp(product, form);
    setSent(true);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        zIndex: 10000,
        display: "flex", alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 24,
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 32px 80px rgba(0,0,0,0.35)",
          animation: "slideUpModal 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {!sent ? (
          <>
            {/* Header */}
            <div style={{
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, #FF3D00 100%)`,
              margin: 16, borderRadius: 16,
              padding: "20px 24px",
              position: "relative",
            }}>
              <button onClick={onClose} style={{
                position: "absolute", top: 12, right: 12,
                background: "rgba(255,255,255,0.2)", border: "none",
                color: "#fff", width: 28, height: 28, borderRadius: "50%",
                cursor: "pointer", fontSize: 14,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>✕</button>
              <div style={{ fontFamily: "'Nunito', sans-serif", color: "rgba(255,255,255,0.75)", fontSize: 10, fontWeight: 800, letterSpacing: 2, marginBottom: 6 }}>SOLICITAR ENCOMENDA</div>
              <div style={{ fontFamily: "'Nunito', sans-serif", color: "#fff", fontSize: 16, fontWeight: 900, marginBottom: 10, paddingRight: 32 }}>{product.nome}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 24, fontWeight: 900, color: "#fff" }}>
                  Kz {formatPrice(product.preco)}
                </span>
                <span style={{
                  background: "rgba(255,255,255,0.25)", color: "#fff",
                  fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                  fontFamily: "'Nunito', sans-serif",
                }}>-{discount}% OFF</span>
              </div>
            </div>

            {/* Form */}
            <div style={{ padding: "4px 20px 24px" }}>
              <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13, color: COLORS.muted, marginBottom: 20, lineHeight: 1.7 }}>
                Preencha os dados e a sua encomenda será enviada directamente para o nosso WhatsApp. 📲
              </p>

              <ModalInput field="name" label="Nome Completo *" placeholder="Seu nome completo" value={form.name} onChange={handleFieldChange} error={errors.name} />
              <ModalInput field="phone" label="WhatsApp / Telefone *" placeholder="+244 9XX XXX XXX" type="tel" value={form.phone} onChange={handleFieldChange} error={errors.phone} />
              <ModalInput field="address" label="Endereço de Entrega *" placeholder="Bairro, rua, número, referência..." value={form.address} onChange={handleFieldChange} error={errors.address} />

              {/* Quantity */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, color: COLORS.text, display: "block", marginBottom: 8 }}>Quantidade</label>
                <div style={{ display: "flex", alignItems: "center", gap: 0, background: "#F4F6FA", borderRadius: 12, padding: 4, width: "fit-content" }}>
                  <button
                    onClick={() => setForm({ ...form, qty: Math.max(1, form.qty - 1) })}
                    style={{
                      width: 40, height: 40, borderRadius: 8, border: "none",
                      background: form.qty === 1 ? "transparent" : "#fff",
                      color: COLORS.text, fontSize: 20, fontWeight: 900,
                      cursor: "pointer", transition: "all 0.2s",
                      boxShadow: form.qty === 1 ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >−</button>
                  <span style={{
                    width: 56, textAlign: "center",
                    fontFamily: "'Nunito', sans-serif", fontSize: 18, fontWeight: 900,
                    color: COLORS.primary,
                  }}>{form.qty}</span>
                  <button
                    onClick={() => setForm({ ...form, qty: form.qty + 1 })}
                    style={{
                      width: 40, height: 40, borderRadius: 8, border: "none",
                      background: "#fff", color: COLORS.primary, fontSize: 20, fontWeight: 900,
                      cursor: "pointer", transition: "all 0.2s",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >+</button>
                </div>
              </div>

              {/* Note */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800, color: COLORS.text, display: "block", marginBottom: 6 }}>Observação (opcional)</label>
                <textarea
                  placeholder="Cor, tamanho, ou qualquer detalhe adicional..."
                  value={form.note}
                  onChange={e => setForm({ ...form, note: e.target.value })}
                  rows={2}
                  style={{
                    width: "100%", padding: "12px 16px", borderRadius: 10,
                    border: `2px solid ${COLORS.border}`, outline: "none",
                    fontFamily: "'Nunito', sans-serif", fontSize: 13,
                    color: COLORS.text, boxSizing: "border-box",
                    resize: "none", transition: "border-color 0.2s",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.primary}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                />
              </div>

              {/* Total */}
              <div style={{
                background: "#FFF7ED", border: "2px solid #FED7AA",
                borderRadius: 14, padding: "14px 18px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 20,
              }}>
                <div>
                  <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 11, color: "#92400E", fontWeight: 700, marginBottom: 2 }}>TOTAL ESTIMADO</div>
                  <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 11, color: "#B45309" }}>{form.qty}x Kz {formatPrice(product.preco)}</div>
                </div>
                <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: 22, fontWeight: 900, color: COLORS.primary }}>
                  Kz {formatPrice(total)}
                </span>
              </div>

              <button
                onClick={handleSubmit}
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #25D366, #128C7E)",
                  border: "none", color: "#fff",
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 15, fontWeight: 900,
                  padding: "16px", borderRadius: 14,
                  cursor: "pointer",
                  boxShadow: "0 8px 24px rgba(37,211,102,0.35)",
                  letterSpacing: 0.3,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Enviar pelo WhatsApp
              </button>

              <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 11, color: COLORS.muted, textAlign: "center", marginTop: 10, lineHeight: 1.6 }}>
                Ao clicar, será redirecionado para o WhatsApp com a sua encomenda já preenchida.
              </p>
            </div>
          </>
        ) : (
          <div style={{ padding: "40px 32px", textAlign: "center" }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: "linear-gradient(135deg, #25D366, #128C7E)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", fontSize: 32,
              boxShadow: "0 12px 32px rgba(37,211,102,0.4)",
            }}>✓</div>
            <h3 style={{ fontFamily: "'Nunito', sans-serif", fontSize: 20, fontWeight: 900, color: COLORS.text, marginBottom: 12 }}>
              WhatsApp aberto! 🎉
            </h3>
            <p style={{ fontFamily: "'Nunito', sans-serif", color: COLORS.muted, lineHeight: 1.8, marginBottom: 28, fontSize: 13 }}>
              A mensagem com os detalhes da encomenda de <strong>{product.nome}</strong> foi gerada. Basta enviar no WhatsApp!
            </p>
            <button onClick={onClose} style={{
              background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
              border: "none", color: "#fff",
              fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800,
              padding: "14px 40px", borderRadius: 50, cursor: "pointer",
              boxShadow: "0 8px 24px rgba(255,107,0,0.4)",
            }}>Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todos");
  const [sort, setSort] = useState("default");
  const [modal, setModal] = useState(null);
  const [mounted, setMounted] = useState(false);

  // Fetch products
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideUpModal { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { background: #F4F6FA; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: #FF6B00; border-radius: 10px; }
      .product-grid-enter { animation: fadeIn 0.4s ease forwards; }
    `;
    document.head.appendChild(style);

    const load = async () => {
      try {
        const data = await Api.ListProduto();
        setProducts(data);
      } catch (err) {
        setError("Não foi possível carregar os produtos. Tente novamente.");
      } finally {
        setLoading(false);
        setTimeout(() => setMounted(true), 80);
      }
    };
    load();
  }, []);

  // Build categories dynamically
  const categories = ["Todos", ...new Set(products.map(p => p.categoria))];

  // Filter + sort
  let filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = p.nome.toLowerCase().includes(q) || p.descricao.toLowerCase().includes(q) || p.categoria.toLowerCase().includes(q);
    const matchCat = category === "Todos" || p.categoria === category;
    return matchSearch && matchCat;
  });
  if (sort === "price-asc") filtered = [...filtered].sort((a, b) => parseFloat(a.preco) - parseFloat(b.preco));
  if (sort === "price-desc") filtered = [...filtered].sort((a, b) => parseFloat(b.preco) - parseFloat(a.preco));
  if (sort === "discount") filtered = [...filtered].sort((a, b) => calcDiscount(b.preco, b.anterior) - calcDiscount(a.preco, a.anterior));

  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg,
      fontFamily: "'Nunito', sans-serif",
      opacity: mounted ? 1 : 0,
      transition: "opacity 0.5s ease",
    }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        background: "#fff",
        boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
        padding: "0 16px",
        display: "flex", alignItems: "center", gap: 12,
        position: "sticky", top: 0, zIndex: 200,
        height: 64,
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{
            width: 38, height: 38,
            background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
            borderRadius: 10,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#fff",
            boxShadow: "0 4px 12px rgba(255,107,0,0.4)",
          }}>S</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: COLORS.text, letterSpacing: -0.5, lineHeight: 1.1 }}>SATEC</div>
            <div style={{ fontSize: 8, color: COLORS.primary, fontWeight: 700, letterSpacing: 2 }}>IMPORTS</div>
          </div>
        </div>

        {/* Search */}
        <div style={{
          flex: 1,
          display: "flex",
          background: "#F4F6FA",
          border: "2px solid #E5E7EB",
          borderRadius: 50,
          overflow: "hidden",
        }}>
          <span style={{ padding: "0 0 0 14px", display: "flex", alignItems: "center", color: COLORS.muted, fontSize: 14 }}>🔍</span>
          <input
            type="text"
            placeholder="Pesquisar produtos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, border: "none", background: "transparent",
              padding: "10px 14px", fontSize: 13, outline: "none",
              color: COLORS.text, fontFamily: "'Nunito', sans-serif",
            }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", padding: "0 12px", color: COLORS.muted, cursor: "pointer", fontSize: 16 }}>✕</button>
          )}
        </div>

        {/* WhatsApp CTA */}
        <a
          href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=Olá`}
          target="_blank"
          rel="noreferrer"
          style={{
            background: "linear-gradient(135deg, #25D366, #128C7E)",
            color: "#fff", textDecoration: "none",
            fontFamily: "'Nunito', sans-serif",
            fontSize: 11, fontWeight: 800,
            padding: "10px 14px", borderRadius: 50,
            flexShrink: 0,
            boxShadow: "0 4px 14px rgba(37,211,102,0.35)",
            display: "flex", alignItems: "center", gap: 5,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Fale connosco
        </a>
      </nav>

      {/* ── HERO ── */}
      <div style={{
        background: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
        padding: "32px 20px 36px",
        position: "relative", overflow: "hidden",
        textAlign: "center",
      }}>
        <div style={{ position: "absolute", top: -60, right: -60, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,107,0,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,102,255,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{
          display: "inline-block",
          background: "rgba(255,107,0,0.15)",
          border: "1px solid rgba(255,107,0,0.3)",
          color: COLORS.primary, fontSize: 11, fontWeight: 800,
          padding: "5px 16px", borderRadius: 20,
          fontFamily: "'Nunito', sans-serif",
          letterSpacing: 2, marginBottom: 16,
        }}>🚀 IMPORTAÇÃO DIRECTA</div>

        <h1 style={{
          fontSize: "clamp(28px, 8vw, 52px)",
          fontWeight: 900, color: "#fff",
          lineHeight: 1.15, marginBottom: 14, letterSpacing: -0.5,
        }}>
          Tecnologia{" "}
          <span style={{
            background: "linear-gradient(90deg, #FF6B00, #FFD600)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>Premium</span>
          <br />ao seu alcance
        </h1>

        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontFamily: "'Nunito', sans-serif", fontWeight: 600, marginBottom: 24, lineHeight: 1.7 }}>
          Escolha o produto, preencha os dados e envie pelo WhatsApp.
        </p>

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="#products" style={{
            background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
            color: "#fff", textDecoration: "none",
            fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 14,
            padding: "14px 28px", borderRadius: 50,
            boxShadow: "0 8px 24px rgba(255,107,0,0.4)",
          }}>🛒 Ver Produtos</a>
          <a href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent("Olá! Preciso de ajuda com uma encomenda.")}`}
            target="_blank" rel="noreferrer"
            style={{
              background: "rgba(255,255,255,0.1)",
              color: "#fff", textDecoration: "none",
              fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 14,
              padding: "14px 28px", borderRadius: 50,
              border: "1px solid rgba(255,255,255,0.2)",
            }}>💬 Falar connosco</a>
        </div>
      </div>

      {/* ── CATEGORIES ── */}
      <div style={{
        background: "#fff",
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        padding: "12px 16px",
        display: "flex", gap: 8, overflowX: "auto",
        scrollbarWidth: "none",
      }}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            style={{
              background: category === cat
                ? `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`
                : "#F4F6FA",
              border: "none",
              color: category === cat ? "#fff" : COLORS.muted,
              fontFamily: "'Nunito', sans-serif",
              fontSize: 12, fontWeight: 800,
              padding: "9px 18px", borderRadius: 50,
              cursor: "pointer", transition: "all 0.2s ease",
              whiteSpace: "nowrap",
              boxShadow: category === cat ? "0 4px 14px rgba(255,107,0,0.4)" : "none",
            }}
          >{cat}</button>
        ))}
      </div>

      {/* ── PRODUCTS ── */}
      <main id="products" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px 60px" }}>
        {/* Header + sort */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: COLORS.text }}>
              {category === "Todos" ? "Todos os Produtos" : category}
            </h2>
            <p style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
              {loading ? "A carregar..." : `${filtered.length} produto${filtered.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            style={{
              border: `2px solid ${COLORS.border}`, borderRadius: 10,
              padding: "10px 14px", fontFamily: "'Nunito', sans-serif",
              fontSize: 13, fontWeight: 700, color: COLORS.text,
              background: "#fff", outline: "none", cursor: "pointer",
            }}
          >
            <option value="default">Relevância</option>
            <option value="price-asc">Menor Preço</option>
            <option value="price-desc">Maior Preço</option>
            <option value="discount">Maior Desconto</option>
          </select>
        </div>

        {/* Active filters */}
        {(category !== "Todos" || search) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {category !== "Todos" && (
              <button onClick={() => setCategory("Todos")} style={{ background: "#FEF3C7", border: "none", color: "#92400E", fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20, cursor: "pointer" }}>{category} ✕</button>
            )}
            {search && (
              <button onClick={() => setSearch("")} style={{ background: "#EDE9FE", border: "none", color: "#5B21B6", fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20, cursor: "pointer" }}>"{search}" ✕</button>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 20, border: "2px dashed #FCA5A5" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 14, color: "#EF4444", fontWeight: 700 }}>{error}</p>
            <button onClick={() => window.location.reload()} style={{ background: COLORS.primary, border: "none", color: "#fff", fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800, padding: "12px 28px", borderRadius: 50, cursor: "pointer", marginTop: 16 }}>Tentar novamente</button>
          </div>
        )}

        {/* Loading Skeletons */}
        {loading && !error && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} />)}
          </div>
        )}

        {/* Grid */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {filtered.map((product, idx) => (
              <div key={product.id} className="product-grid-enter" style={{ animationDelay: `${idx * 0.05}s` }}>
                <ProductCard product={product} onOrder={setModal} />
              </div>
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 20, border: `2px dashed ${COLORS.border}` }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🔍</div>
            <h3 style={{ fontFamily: "'Nunito', sans-serif", fontSize: 18, fontWeight: 900, color: COLORS.text, marginBottom: 8 }}>Nenhum produto encontrado</h3>
            <p style={{ fontFamily: "'Nunito', sans-serif", color: COLORS.muted, fontSize: 13 }}>Tente outros termos ou mude a categoria.</p>
            <button onClick={() => { setSearch(""); setCategory("Todos"); }} style={{ background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`, border: "none", color: "#fff", fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800, padding: "12px 28px", borderRadius: 50, cursor: "pointer", marginTop: 16 }}>Limpar filtros</button>
          </div>
        )}

        {/* CTA Banner */}
        {!loading && (
          <div style={{
            marginTop: 48,
            background: "linear-gradient(135deg, #0f0c29 0%, #302b63 100%)",
            borderRadius: 24, padding: "36px 28px",
            textAlign: "center", position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: -30, right: -30, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,107,0,0.18) 0%, transparent 70%)" }} />
            <div style={{ fontSize: 10, color: COLORS.primary, fontWeight: 800, letterSpacing: 3, marginBottom: 10 }}>NÃO ENCONTROU O QUE PROCURA?</div>
            <h3 style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 10, letterSpacing: -0.5 }}>Importamos qualquer produto</h3>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, lineHeight: 1.8, marginBottom: 24, fontWeight: 600 }}>
              Diga-nos o que precisa pelo WhatsApp. A SATEC importa qualquer produto tecnológico do mundo.
            </p>
            <a
              href={`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent("Olá SATEC! Preciso de importar um produto que não encontrei no catálogo.")}`}
              target="_blank" rel="noreferrer"
              style={{
                display: "inline-block",
                background: "linear-gradient(135deg, #25D366, #128C7E)",
                color: "#fff", textDecoration: "none",
                fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 900,
                padding: "16px 36px", borderRadius: 50,
                boxShadow: "0 8px 32px rgba(37,211,102,0.35)",
              }}
            >💬 Falar no WhatsApp</a>
          </div>
        )}
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ background: "#111827", padding: "40px 20px 30px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 12 }}>
            <div style={{ width: 38, height: 38, background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: "#fff" }}>S</div>
            <div style={{ fontWeight: 900, fontSize: 18, color: "#fff" }}>SATEC <span style={{ fontSize: 9, color: COLORS.primary, fontWeight: 700, letterSpacing: 2 }}>IMPORTS</span></div>
          </div>
          <p style={{ color: "#6B7280", fontSize: 12, fontFamily: "'Nunito', sans-serif", lineHeight: 1.8, maxWidth: 320, margin: "0 auto" }}>
            Especialistas em importação de tecnologia premium. Ligando você às melhores marcas, directamente em Angola.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap", marginBottom: 28 }}>
          {[{ icon: "💬", text: "+244 946 661 251" }].map(c => (
            <div key={c.text} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span>{c.icon}</span>
              <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "'Nunito', sans-serif", fontWeight: 600 }}>{c.text}</span>
            </div>
          ))}
        </div>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20, textAlign: "center", fontSize: 11, color: "#4B5563", fontFamily: "'Nunito', sans-serif", fontWeight: 600 }}>
          © 2025 SATEC Imports. Todos os direitos reservados.
        </div>
      </footer>

      <OrderModal product={modal} onClose={() => setModal(null)} />
    </div>
  );
}